import classnames from 'classnames'
import * as React from 'react'

import { IModalConfigInput, Position } from '../../../../types/shared'
import { Button, ButtonGroup, Icon, InnerState, Label, Select, SelectOption, TooltipInput } from '../../../components'
import { IndicatorType, SpecificChartFunctionality } from '../../../constants/common'
import Indicator from '../../../indicators/indicator'
import { indicatorsByType } from '../../../indicators/indicators'
import { useForceUpdate } from '../../../utils/force-update'
import { useChartLayoutGlobalModel } from '../chart-layout-model'
import { handleAddIndicatorPane, handleIndicatorReplace, handleRemoveIndicatorPane } from './model-manipulation'

interface IIndicatorType {
  value: IndicatorType
  label: string
}

interface IIndicatorsValue {
  elementInstance: Indicator
  position: Position
  paneIndex: number
  paneId: string
}

const INDICATOR_LIST_PROPS = { className: 'overflow-y-auto max-h-80 ' } // temp solution to page stretching if max-h not provided

export function IndicatorsSelect() {
  const { forceUpdate } = useForceUpdate()
  const { chartLayoutModel } = useChartLayoutGlobalModel()
  const indicators: IIndicatorsValue[] = []

  // Re-render of this component is controlled by forceUpdate()
  // We can't use memo because we need to run it on every time we call forceUpdate,
  // if it is a memo its only dependency is chartLayoutModel and object reference never change so memo won't update
  let chartIndex: number
  chartLayoutModel
    ?.charts()
    .first()
    ?.getAllPanes()
    .forEach((pane, paneIndex) => {
      pane.getAllElements().forEach((element) => {
        if (element.isChart()) {
          chartIndex = paneIndex
        }
        if (element.isIndicator()) {
          indicators.push({
            // @ts-expect-error - Element model expect instance as CanvasElement but can receive CanvaseElement || IndicatorElement
            elementInstance: element.instance,
            position: chartIndex === undefined ? Position.Above : Position.Below,
            paneIndex: paneIndex,
            paneId: pane.id,
          })
        }
      })
    })
  indicators.sort((a, b) => Number(a.paneId.replace('c-', '')) - Number(b.paneId.replace('c-', '')))

  const DEFAULT_INDICATORS = React.useMemo(() => {
    if (!chartLayoutModel) return []
    const isFeaturesPage = chartLayoutModel.specificChartFunctionality === SpecificChartFunctionality.featuresPage
    const ignoredIndicators = [IndicatorType.None, IndicatorType.Indicator, !isFeaturesPage && IndicatorType.Cot]

    return Object.keys(indicatorsByType)
      .filter((item) => !ignoredIndicators.filter(Boolean).includes(item as IndicatorType))
      .map((item) => ({ value: item as IndicatorType, label: indicatorsByType[item as IndicatorType].label }))
  }, [chartLayoutModel])

  const onIndicatorSelect = React.useCallback(
    ({ value, paneIndex }: { value: IndicatorType; paneIndex: number }) => {
      if (chartLayoutModel) {
        handleIndicatorReplace({ value, paneIndex, chartLayoutModel })
        forceUpdate()
      }
    },
    [chartLayoutModel, forceUpdate]
  )

  const onIndicatorDelete = React.useCallback(
    (paneIndex: number) => {
      if (chartLayoutModel) {
        handleRemoveIndicatorPane({ paneIndex, chartLayoutModel })
        forceUpdate()
      }
    },
    [chartLayoutModel, forceUpdate]
  )

  const onIndicatorAdd = React.useCallback(() => {
    if (chartLayoutModel) {
      handleAddIndicatorPane({ chartLayoutModel })
      // because of race condition if we call forceUpdate without timeout new select won't appear in setting dialog
      setTimeout(() => forceUpdate())
    }
  }, [chartLayoutModel, forceUpdate])

  const onIndicatorValueChange = ({
    value,
    paneIndex,
    indicatorInput,
  }: {
    value: string
    paneIndex: number
    indicatorInput: IModalConfigInput
  }) => {
    if (chartLayoutModel) {
      const charts = chartLayoutModel.getAllCharts()
      charts.forEach((chart) => {
        const indicatorElement = chart
          .getAllPanes()
          [paneIndex].getAllElements()
          .find((el) => el.isIndicator())
        if (!indicatorElement) return
        indicatorElement.instance.set({
          [indicatorInput.name]: indicatorInput.type === 'number' ? Number(value) : value,
        })
      })
    }
  }

  const onIndicatorPositionChange = React.useCallback(
    ({ direction, paneIndex, currentPosition }: { direction: string; paneIndex: number; currentPosition: string }) => {
      if (chartLayoutModel && currentPosition !== direction) {
        const charts = chartLayoutModel.getAllCharts()
        charts.forEach((chart) => {
          const panes = chart.getAllPanes()
          if (direction === Position.Above) {
            panes[paneIndex].move(Position.Above)
          }

          if (direction === Position.Below) {
            panes[paneIndex].move(Position.Below)
          }
        })

        forceUpdate()
      }
    },
    [chartLayoutModel, forceUpdate]
  )

  if (!chartLayoutModel) return null

  return (
    <>
      <Label title="Indicators" />
      <div className="mb-2 space-y-6 xs:space-y-2">
        {indicators.map((indicatorObj, indicatorIndex) => {
          const { elementInstance, position, paneIndex } = indicatorObj
          const indicatorConfig = elementInstance.getModalConfig()
          const { inputsErrorMessages } = indicatorConfig
          return (
            <div
              className={classnames('grid gap-1', {
                'xs:grid-cols-[0.6fr_1fr]': indicatorConfig.inputs,
                'grid-cols-[1fr_2rem]': !indicatorConfig.inputs,
              })}
              key={`${elementInstance.name}-${paneIndex}`}
            >
              <Select
                aria-label={elementInstance.label}
                data-testid={`indicator-${indicatorIndex}-select`}
                items={DEFAULT_INDICATORS}
                labelClassName="overflow-hidden -m-0.5 p-0.5"
                placeholder="Select indicator"
                listProps={INDICATOR_LIST_PROPS}
                size="medium"
                value={elementInstance.type}
                onChange={(item: IIndicatorType) => {
                  onIndicatorSelect({ value: item.value, paneIndex })
                }}
                hasPopoverButtonWidthLimit={false}
              >
                {DEFAULT_INDICATORS.map((item) => (
                  <SelectOption data-testid={`indicator-${indicatorIndex}-select-${item.value}`} key={item.value}>
                    {item.label}
                  </SelectOption>
                ))}
              </Select>

              <div className="grid grid-cols-[1fr_2rem] gap-1" key={`${elementInstance.name}-${paneIndex}`}>
                {indicatorConfig.inputs && (
                  <div className="grid flex-1 grid-cols-[repeat(3,1fr)_auto] gap-1">
                    {elementInstance.type !== IndicatorType.Perf && indicatorConfig.inputs.length < 3 && (
                      <div
                        className="flex items-center"
                        style={{ gridColumn: `span ${3 - indicatorConfig.inputs.length}` }}
                      >
                        <div className="h-px w-full bg-gray-200 dark:bg-gray-700" />
                      </div>
                    )}
                    {indicatorConfig.inputs.map((indicatorInput, confIndex) => (
                      <div
                        key={`${confIndex}-${elementInstance.type}-${paneIndex}`}
                        style={
                          elementInstance.type === IndicatorType.Perf
                            ? { gridColumn: 'span 3' }
                            : { gridColumnStart: 4 - (indicatorConfig.inputs.length - confIndex) }
                        }
                      >
                        <InnerState
                          {...indicatorInput}
                          aria-label={indicatorInput.label}
                          as={TooltipInput}
                          data-testid={`indicator-${indicatorIndex}-input-${confIndex}`}
                          label={undefined}
                          size="medium"
                          inputClass={classnames({ 'text-right': indicatorInput.type === 'number' })}
                          error={!elementInstance.getIsValid(indicatorInput.name)}
                          onBlur={forceUpdate}
                          valueAccessor={(e: React.ChangeEvent<HTMLInputElement>) => e.target.value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            onIndicatorValueChange({ value: e.target.value, paneIndex, indicatorInput })
                          }}
                          onInvalid={(e: React.BaseSyntheticEvent<HTMLInputElement>) => {
                            e.target.focus()
                          }}
                          tooltipContent={
                            <div>
                              <div>{indicatorInput.label}</div>
                              {!elementInstance.getIsValid(indicatorInput.name) && (
                                <div className="text-red-300 dark:text-red-500">
                                  {inputsErrorMessages[indicatorInput.name]}
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    ))}
                    <ButtonGroup className="col-start-4 flex w-16 flex-shrink-0 p-0.5 xs:w-12.5" hasDivider={false}>
                      <Button
                        key={`${indicatorIndex}-above`}
                        className="h-full grow"
                        contentClass="flex justify-center"
                        data-testid={`indicator-${indicatorIndex}-position-above-btn`}
                        rounding="small"
                        size="none"
                        type="button"
                        theme={position === Position.Above ? 'blue' : undefined}
                        onClick={() => {
                          onIndicatorPositionChange({ direction: Position.Above, paneIndex, currentPosition: position })
                        }}
                      >
                        <Icon name="tabKey" className="-rotate-90" />
                      </Button>
                      <Button
                        key={`${indicatorIndex}-below`}
                        className="h-full grow"
                        contentClass="flex justify-center"
                        data-testid={`indicator-${indicatorIndex}-position-below-btn`}
                        rounding="small"
                        size="none"
                        type="button"
                        theme={position === Position.Below ? 'blue' : undefined}
                        onClick={() => {
                          onIndicatorPositionChange({
                            direction: Position.Below,
                            paneIndex,
                            currentPosition: position,
                          })
                        }}
                      >
                        <Icon name="tabKey" className="rotate-90" />
                      </Button>
                    </ButtonGroup>
                  </div>
                )}
                <Button
                  appearance="square"
                  data-testid={`indicator-${indicatorIndex}-delete`}
                  size="medium"
                  type="button"
                  onClick={() => onIndicatorDelete(paneIndex)}
                >
                  <Icon name="trashCan" />
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      {indicators.length < 6 && (
        <Button
          leftContent="add"
          size="medium"
          type="button"
          onClick={onIndicatorAdd}
          data-testid="add-new-indicator-btn"
        >
          Indicators
        </Button>
      )}
    </>
  )
}
